import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaStar, FaFilter } from 'react-icons/fa';
import { useTranslation } from '@/contexts/TranslationContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const districts = [
  'All districts',
  'Vake',
  'Vera',
  'Sololaki',
  'Mtatsminda',
  'Chughureti',
  'Saburtalo',
  'Digomi'
];

const venueTypes = [
  'All types',
  'Café',
  'Restaurant',
  'Bar',
  'Hotel',
  'Hostel',
  'Park',
  'Shop',
  'Petrol Station',
  'Dessert Shop / Cafe',
  'Other'
];

const VenueFinder = () => {
  const { t } = useTranslation();
  const [venues, setVenues] = useState([]);
  const [filteredVenues, setFilteredVenues] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(t('venues.allDistricts'));
  const [selectedType, setSelectedType] = useState(t('venues.allTypes'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await axios.get(`${API}/venues`);
        setVenues(response.data);
        setFilteredVenues(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching venues:', error);
        setLoading(false);
      }
    };
    fetchVenues();
  }, []);

  useEffect(() => {
    let filtered = venues;

    if (selectedDistrict !== t('venues.allDistricts') && selectedDistrict !== 'All districts') {
      filtered = filtered.filter(v => v.district === selectedDistrict);
    }

    if (selectedType !== t('venues.allTypes') && selectedType !== 'All types') {
      filtered = filtered.filter(v => v.venue_type === selectedType);
    }

    setFilteredVenues(filtered);
  }, [selectedDistrict, selectedType, venues]);

  return (
    <div id="hosts" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold text-[#1A1C19] mb-4">
            {t('venues.title')}
          </h2>
          <p className="font-['Manrope'] text-xl text-[#5E635C]">
            {t('venues.subtitle')}
          </p>
        </motion.div>

        {/* Filters */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4 items-center justify-center">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-500" />
            <select
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
              data-testid="district-filter"
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
            >
              {districts.map(district => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              data-testid="type-filter"
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
            >
              {venueTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Venues Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVenues.map((venue, index) => (
              <motion.div
                key={venue.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                data-testid={`venue-card-${venue.id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={venue.image_url}
                    alt={venue.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {venue.name}
                  </h3>

                  <div className="flex items-center gap-2 text-gray-600 text-sm mb-3">
                    <FaMapMarkerAlt className="text-emerald-600" />
                    <span>{venue.address}</span>
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <FaStar className="text-yellow-400" />
                    <span className="font-semibold text-gray-900">{venue.rating}</span>
                    <span className="text-gray-500 text-sm">({venue.review_count})</span>
                  </div>

                  {venue.pet_friendly_features.length > 0 && (
                    <ul className="space-y-1 mb-4">
                      {venue.pet_friendly_features.slice(0, 2).map((feature, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                          <span className="text-emerald-600 mt-1">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {venue.dogs_cared.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500">
                        Cares for {venue.dogs_cared.length} {venue.dogs_cared.length === 1 ? 'dog' : 'dogs'}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {!loading && filteredVenues.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No venues found with the selected filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueFinder;
